// React imports
import { useCallback, useEffect, useMemo, useState } from 'react';

// Third-party imports
import cloneDeep from 'lodash-es/cloneDeep';
import debounce from 'lodash-es/debounce';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import set from 'lodash-es/set';
import unset from 'lodash-es/unset';
import { CircleHelpIcon } from 'lucide-react';
import { FieldError, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ToolkitEditor } from '@/components/organisms/Toolkit/ToolkitEditor/ToolkitEditor';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import { VariantSelector } from '@/components/ui/custom/VariantSelector';
import { Label as ShadcnLabel } from '@/components/ui/label';
// Hook imports
import { useBrainVariables } from '@/hooks/useBrainVariables';
import i18n from '@/i18n/i18n';
import { cn } from '@/lib/utils';
// Type imports
import { OptionBase } from '@/shared/types/common';
import { Requisite, RequisiteOptionType } from '@/shared/types/node';
import { QUESTION_DOCS_URL } from '@/util/constants';
import { createYupResolver } from '@/util/forms/createYupResolver';
// Utility imports
import { capitalizeFirstLetter, formatErrorMessage } from '@/util/util';
import { LENGTH_XXL, LENGTH_XL } from '@/util/validation/constants';

// Local imports
import { INPUT_TYPES } from './helper';
import { QuickOptions } from './QuickOptions';

const checkForSchema = yup.object().shape({
  check_for: yup.string().defined(),
  tabs: yup.array().of(
    yup.object({
      tabName: yup.string().required(),
      maxCharacters: yup.number().required(),
      textValue: yup
        .string()
        .trim()
        .max(LENGTH_XXL, () => i18n.t('rich_text_editor.errors.char_limit')),
    })
  ),
  validation_guideline: yup
    .string()
    .max(LENGTH_XL, () => i18n.t('validation.less_than', { 0: LENGTH_XL })),
});

// ===== TYPES =====

/**
 * Form data structure for the question check and ask component
 * Inferred from the checkForSchema yup schema
 */
type FormType = yup.InferType<typeof checkForSchema>;

/**
 * Option data containing the type information
 */
interface CheckForOptionData {
  type: string;
}

/**
 * Props for the QuestionCheckAndAsk component
 */
interface Props {
  /** The requisite object containing question configuration */
  requisite: Requisite;
  /** Callback function to update the requisite */
  onRequisiteUpdate: (req: Requisite) => void;
  /** Available options for the component */
  options: OptionBase[];
  /** Function to update error states */
  updateErrors: (key: string, error: string | null) => void;
}

/**
 * QuestionCheckAndAsk component for handling question configuration with check and ask functionality
 *
 * @param props - Component props
 * @returns JSX element
 */
const QuestionCheckAndAsk = ({ onRequisiteUpdate, requisite, updateErrors }: Props) => {
  // ===== HOOKS =====
  const { entityVariables, variables } = useBrainVariables();
  const { t } = useTranslation();
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // ===== COMPUTED VALUES =====
  /**
   * Sorted list of variables combining input types and entity variables
   * Mapped to AutocompleteOption format with category for grouping
   */
  const variableOptions = useMemo((): AutocompleteOption<CheckForOptionData>[] => {
    const allOptions = [...INPUT_TYPES, ...entityVariables].sort((a, b) => {
      if (a.label.startsWith('@')) {
        return 1;
      }
      return a.label.localeCompare(b.label);
    });

    return allOptions.map((option) => ({
      value: option.value,
      label: option.label,
      category: t(`auto_complete.group_labels.${option.type}`),
      data: { type: option.type },
    }));
  }, [entityVariables, t]);

  /**
   * Options for the requisite actions
   */
  const requisiteOptions: RequisiteOptionType[] = useMemo(
    () => get(requisite, 'actions[0].options', []),
    [requisite]
  );

  /**
   * Check for existing validation or check_for value
   */
  const checkForExists = useMemo(() => requisite?.check_for ?? requisite?.validate, [requisite]);

  /**
   * Default check for value (just the value string, FormAutocomplete handles lookup)
   */
  const defaultCheckFor = useMemo(() => checkForExists ?? '', [checkForExists]);

  // ===== FORM CONFIGURATION =====
  const {
    control,
    formState: { errors },
    trigger,
    setValue,
    getValues,
  } = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      check_for: defaultCheckFor,
      tabs: [
        {
          textValue: requisite?.actions?.[0]?.texts?.[0] ?? '',
          tabName: t('actions.types.question'),
          maxCharacters: LENGTH_XXL,
        },
        {
          textValue: requisite?.reprompt?.[0]?.texts?.[0] ?? '',
          tabName: t('actions.types.reprompt'),
          maxCharacters: LENGTH_XXL,
        },
      ],
      validation_guideline: requisite?.validation_guideline || '',
    },
    resolver: createYupResolver<FormType>(checkForSchema),
  });

  const checkForValue = useWatch({ control, name: 'check_for' });
  const validationGuidelineValue = useWatch({ control, name: 'validation_guideline' });

  /**
   * Determine if the selected check_for value is an entity type
   * We look up the option from variableOptions to get the type data
   */
  const isEntitySelected = useMemo(() => {
    const selectedOption = variableOptions.find((opt) => opt.value === checkForValue);
    return selectedOption?.data?.type === 'entity' || checkForValue?.startsWith('@');
  }, [checkForValue, variableOptions]);

  // Initialize form from Redux
  useEffect(() => {
    setValue('tabs.0.textValue', requisite?.actions?.[0]?.texts?.[0] ?? '');
    setValue('tabs.1.textValue', requisite?.reprompt?.[0]?.texts?.[0] ?? '');
  }, [requisite?.actions, requisite?.reprompt, setValue]);

  // ===== ERROR HANDLING =====
  /**
   * Formatted check for error message
   */
  const checkForErrorMessage = useMemo(
    () => formatErrorMessage(errors?.check_for as unknown as FieldError),
    [errors?.check_for]
  );

  /**
   * Validation guideline error message
   */
  const validationGuidelineErrorMessage = useMemo(
    () => capitalizeFirstLetter(errors?.validation_guideline?.message),
    [errors?.validation_guideline?.message]
  );

  // ===== CALLBACKS =====
  /**
   * Handles changes to the check for combobox selection
   * @param value - Selected value string
   */
  const handleCheckForChange = useCallback(
    (value: string) => {
      // Look up the option to determine if it's an entity type
      const selectedOption = variableOptions.find((opt) => opt.value === value);
      const isSelectedOptionEntity =
        selectedOption?.data?.type === 'entity' || value?.startsWith('@');

      const newRequisite = cloneDeep(requisite);

      if (isSelectedOptionEntity) {
        set(newRequisite, 'check_for', value ?? '');
        unset(newRequisite, 'validate');
      } else {
        unset(newRequisite, 'check_for');
        set(newRequisite, 'validate', value ?? '');
      }

      onRequisiteUpdate(newRequisite);
    },
    [onRequisiteUpdate, requisite, variableOptions]
  );

  /**
   * Handles changes to tab content (question and reprompt)
   */
  const handleTabOnChange = useCallback(() => {
    const tabs = getValues('tabs') ?? [];
    const [questionTab, repromptTab] = tabs;
    if (!questionTab || !repromptTab) {
      return;
    }
    const questionText = questionTab.textValue;
    const repromptText = repromptTab.textValue;

    const currentQuestionText = requisite?.actions?.[0]?.texts?.[0] ?? '';
    const currentRepromptText = requisite?.reprompt?.[0]?.texts?.[0] ?? '';
    const currentQuestionType = requisite?.actions?.[0]?.type;
    const currentRepromptType = requisite?.reprompt?.[0]?.type;

    const isQuestionChanged = !isEqual(questionText, currentQuestionText);
    const isRepromptChanged = !isEqual(repromptText, currentRepromptText);
    const needsQuestionTypeUpdate = questionText !== '' && currentQuestionType !== 'text';
    const needsRepromptTypeUpdate = repromptText !== '' && currentRepromptType !== 'text';

    if (
      !isQuestionChanged &&
      !isRepromptChanged &&
      !needsQuestionTypeUpdate &&
      !needsRepromptTypeUpdate
    ) {
      return;
    }

    // Clone and Initialization
    const newRequisite = cloneDeep(requisite);

    // Always set action type to 'text'
    set(newRequisite, 'actions[0].type', 'text');

    // Update question text
    if (isQuestionChanged || needsQuestionTypeUpdate) {
      if (questionText === '') {
        set(newRequisite, 'actions', undefined);
      } else {
        set(newRequisite, 'actions[0].texts[0]', questionText);
        if (requisiteOptions.length > 0) {
          set(newRequisite, 'actions[0].options', requisiteOptions);
        }
      }
    }

    // Update reprompt text
    if (isRepromptChanged || needsRepromptTypeUpdate) {
      if (repromptText === '') {
        set(newRequisite, 'reprompt', undefined);
      } else {
        set(newRequisite, 'reprompt[0].type', 'text');
        set(newRequisite, 'reprompt[0].texts[0]', repromptText);
        if (requisiteOptions.length > 0) {
          set(newRequisite, 'reprompt[0].options', requisiteOptions);
        }
      }
    }

    // Update if there are changes
    if (!isEqual(JSON.stringify(newRequisite), JSON.stringify(requisite))) {
      onRequisiteUpdate(newRequisite);
    }
  }, [requisite, getValues, requisiteOptions, onRequisiteUpdate]);

  const debouncedHandleTabOnChange = useMemo(
    () => debounce(handleTabOnChange, 200),
    [handleTabOnChange]
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedHandleTabOnChange.cancel();
    };
  }, [debouncedHandleTabOnChange]);

  const handleEditorChange = useCallback(
    (index: number, content: string) => {
      setValue(`tabs.${index}.textValue`, content, { shouldValidate: true });
      debouncedHandleTabOnChange();
    },
    [setValue, debouncedHandleTabOnChange]
  );

  /**
   * Handles changes to the validation guideline editor
   * @param content - New content from editor
   */
  const handleGuidelineOnChange = useCallback(
    (content: string) => {
      setValue('validation_guideline', content, { shouldValidate: true });

      // Only update parent/Redux if content actually changed from original
      const originalValue = requisite?.validation_guideline || '';
      if (content === originalValue) {
        return;
      }

      const newRequisite = cloneDeep(requisite);
      if (content === '') {
        unset(newRequisite, 'validation_guideline');
      } else {
        set(newRequisite, 'validation_guideline', content);
      }
      onRequisiteUpdate(newRequisite);
    },
    [requisite, onRequisiteUpdate, setValue]
  );

  /**
   * Handles changes to the ignore_context switch
   * @param checked - New checked state
   */
  const handleIgnoreContextChange = useCallback(
    (checked: boolean) => {
      onRequisiteUpdate({ ...requisite, ignore_context: checked || undefined });
    },
    [requisite, onRequisiteUpdate]
  );

  // ===== EFFECTS =====
  /**
   * Trigger form validation on component mount
   */
  useEffect(() => {
    trigger();
  }, [trigger]);

  /**
   * Update check_for error state
   */
  useEffect(() => {
    updateErrors('check_for', checkForErrorMessage);
  }, [checkForErrorMessage, updateErrors]);

  /**
   * Update Question tab error state
   */
  useEffect(() => {
    const errorMessage = errors?.tabs?.[0]?.textValue?.message;
    updateErrors('Question', errorMessage ? capitalizeFirstLetter(errorMessage) : null);
  }, [errors?.tabs, updateErrors]);

  /**
   * Update Reprompt tab error state
   */
  useEffect(() => {
    const errorMessage = errors?.tabs?.[1]?.textValue?.message;
    updateErrors('Reprompt', errorMessage ? capitalizeFirstLetter(errorMessage) : null);
  }, [errors?.tabs, updateErrors]);

  /**
   * Update validation guideline error state
   */
  useEffect(() => {
    updateErrors('validation_guideline', validationGuidelineErrorMessage);
  }, [validationGuidelineErrorMessage, updateErrors]);

  // ===== TAB UI =====
  const watchedTabs = useWatch({ control, name: 'tabs' }) ?? [];
  const hasError = useMemo(
    () => !isEmpty(errors?.tabs?.[activeTabIndex]?.textValue),
    [errors?.tabs, activeTabIndex]
  );

  const tabItems = useMemo(
    () => [
      { index: 0, label: t('actions.types.question') },
      { index: 1, label: t('actions.types.reprompt') },
    ],
    [t]
  );

  return (
    <>
      <div className="my-6">
        <div className="mb-4">
          <VariantSelector
            items={tabItems}
            activeIndex={activeTabIndex}
            onSelect={setActiveTabIndex}
            hasError={hasError}
          />
        </div>

        <ToolkitEditor
          content={watchedTabs[activeTabIndex]?.textValue ?? ''}
          onContentChange={(content) => handleEditorChange(activeTabIndex, content)}
          variableOptions={variables}
          maxLength={LENGTH_XXL}
        />
        {errors?.tabs?.[activeTabIndex]?.textValue && (
          <p className="mt-1 text-sm text-destructive">
            {formatErrorMessage(errors.tabs[activeTabIndex].textValue)}
          </p>
        )}

        <QuickOptions requisite={requisite} updateErrors={updateErrors} />
      </div>
      <FormAutocomplete
        name="check_for"
        control={control}
        options={variableOptions}
        label={t('dialog.question.check_if_exists')}
        placeholder={t('dialog.question.select_an_entity')}
        onChange={handleCheckForChange}
        error={!!errors?.check_for}
        errorMessage={checkForErrorMessage}
      />
      {!isEntitySelected && (
        <div className="mt-6">
          <label className="mb-2 flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase">
            {t('dialog.question.guideline_label')}
            <InfoTooltip
              content={t('dialog.question.guideline_description')}
              learnMoreUrl={QUESTION_DOCS_URL}
              side="right"
            >
              <CircleHelpIcon size={12} className="text-muted-foreground" />
            </InfoTooltip>
          </label>
          <ToolkitEditor
            content={validationGuidelineValue || ''}
            onContentChange={handleGuidelineOnChange}
            variableOptions={variables}
            maxLength={LENGTH_XL}
            placeholder={t('dialog.question.guideline_placeholder')}
          />
          <div className="mt-1 flex items-center justify-between">
            {validationGuidelineErrorMessage && (
              <p className="text-sm text-destructive">{validationGuidelineErrorMessage}</p>
            )}
            <p
              className={cn(
                'ml-auto text-sm',
                (validationGuidelineValue?.length || 0) > LENGTH_XL
                  ? 'text-destructive'
                  : 'text-muted-foreground'
              )}
            >
              {validationGuidelineValue?.length || 0}/{LENGTH_XL}
            </p>
          </div>
          <div className="mt-4 flex items-start gap-2 align-middle">
            <Checkbox
              id="ignore_context"
              checked={requisite?.ignore_context ?? false}
              onCheckedChange={handleIgnoreContextChange}
              disabled={!validationGuidelineValue}
              className="mt-0.5"
            />
            <div className="flex flex-col gap-1">
              <ShadcnLabel
                htmlFor="ignore_context"
                className={cn(
                  'text-sm font-medium',
                  validationGuidelineValue ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                )}
              >
                {t('dialog.question.ignore_context_label')}
              </ShadcnLabel>
              <p
                className={cn(
                  'text-sm text-muted-foreground',
                  !validationGuidelineValue && 'opacity-50'
                )}
              >
                {t('dialog.question.ignore_context_description')}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionCheckAndAsk;
